import {
  SettingParam,
  WorkflowNodeSetting,
  WorkflowNodeTypes,
} from "@/types/node";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {capitalize} from "@/utils/index";
import {getSettingValueByKey} from "@/utils/index";
import {addEdge, Edge, Position, useReactFlow} from "@xyflow/react";
import {createFloatingEdge} from "../../pages/workflow/EdgeTypes/FloatingEdge";

export type JumpToOption = {
  name: string;
  value: string;
  type: WorkflowNodeTypes;
};

export const drawFloatingEdge = ({
  sourceNodeId,
  targetNodeId,
  edges,
}: {
  sourceNodeId: string;
  targetNodeId: string;
  edges: Edge[];
}) => {
  // remove all other floating edges for the node
  const newEdges = edges.filter(
    edge => !(edge.source === sourceNodeId && edge.type === "floating"),
  );

  newEdges.push(
    createFloatingEdge({
      sourceNodeId,
      targetNodeId,
    }),
  );

  return newEdges.map(e => ({...e, selected: false}));
};

export const SettingNodeSelect = ({
  param,
  handleChange,
  placeholder,
  selectedNodeId,
  disabled = false,
  ...props
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: string) => void;
  placeholder?: string;
  selectedNodeId: string;
  disabled: boolean;
}) => {
  const {getNodes, getNode, setEdges} = useReactFlow();

  const nodes = getNodes();

  let jumpToOptions: JumpToOption[] = [];

  if (nodes.length > 0) {
    const filteredNodes = nodes.filter(
      node =>
        node.data.type !== WorkflowNodeTypes.JUMP &&
        node.data.type !== WorkflowNodeTypes.PLACEHOLDER &&
        node.data.type !== WorkflowNodeTypes.ROOT &&
        node.data.type !== WorkflowNodeTypes.START &&
        node.data.type !== WorkflowNodeTypes.GLOBAL,
    );

    jumpToOptions = filteredNodes
      .map(node => {
        const nodeSettings = node.data.settings as WorkflowNodeSetting[];
        const name = (
          getSettingValueByKey(nodeSettings, "name") ||
          node.data.settingsTitle ||
          ""
        ).toString();
        return {
          name,
          value: node.id,
          type: node.data.type as WorkflowNodeTypes,
        };
      })
      .sort((a, b) => a.type.localeCompare(b.type));
  }

  const handleValueChange = (value: string) => {
    handleChange(param, value);
    const selectedNode = getNode(selectedNodeId);
    const jumpToNode = getNode(value);

    setEdges((edges: Edge[]) => {
      return drawFloatingEdge({
        sourceNodeId: selectedNode.id,
        targetNodeId: jumpToNode.id,
        edges: edges,
      });
    });
  };

  return (
    <div className="mb-4">
      <label className="text-sm font-medium text-label mb-2 flex items-center justify-between">
        {param.label}
      </label>
      <Select
        onValueChange={value => handleValueChange(value)}
        value={param.value?.toString() || ""}
        disabled={disabled}
      >
        <SelectTrigger
          className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
            disabled ? "pointer-events-none" : ""
          }`}
        >
          <SelectValue placeholder={placeholder || "Select branch or step"} />
        </SelectTrigger>
        <SelectContent className="max-w-[398px]">
          {jumpToOptions.length > 0 &&
            jumpToOptions.map(option => (
              <SelectItem
                value={option.value}
                key={option.value}
                onMouseEnter={() => {
                  setEdges(edges => {
                    return drawFloatingEdge({
                      sourceNodeId: selectedNodeId,
                      targetNodeId: option.value,
                      edges: edges,
                    });
                  });
                }}
                onMouseLeave={() => {
                  setEdges(edges => {
                    return drawFloatingEdge({
                      sourceNodeId: selectedNodeId,
                      targetNodeId: param.value?.toString(),
                      edges: edges,
                    });
                  });
                }}
              >
                <strong>{capitalize(option.type)}: </strong>
                {option.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
};
